// =============================================================================
// LEGAL PAGES CONTENT (TERMS & PRIVACY)
// =============================================================================

import type { ContentComponent } from '@/types';

export const termsPageComponents: ContentComponent[] = [
  {
    id: 'hero-terms',
    type: 'hero',
    title: 'Terms of Service',
    content: 'Please read these terms carefully before using our services.',
    metadata: {
      image: '/images/terms.jpg'
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'terms-content',
    type: 'text',
    title: 'Terms of Service',
    content: `
      <p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>
      
      <h3>1. Acceptance of Terms</h3>
      <p>By accessing and using the services provided by Alex Finger Consulting ("we," "us," or "our"), you accept and agree to be bound by the terms and provision of this agreement.</p>
      
      <h3>2. Description of Service</h3>
      <p>Alex Finger Consulting provides web development, business strategy consulting, and coaching services. These services include but are not limited to:</p>
      <ul>
        <li>Custom web application development</li>
        <li>Business strategy and consulting</li>
        <li>Technical coaching and mentorship</li>
        <li>Project management and guidance</li>
      </ul>
      
      <h3>3. Client Responsibilities</h3>
      <p>As a client, you agree to:</p>
      <ul>
        <li>Provide accurate and complete information about your project requirements</li>
        <li>Respond to requests for feedback and approval in a timely manner</li>
        <li>Make payments according to the agreed schedule</li>
        <li>Respect intellectual property rights and confidentiality</li>
      </ul>
      
      <h3>4. Payment Terms</h3>
      <p>Payment terms will be specified in individual project agreements. Generally:</p>
      <ul>
        <li>Projects require a deposit before work begins</li>
        <li>Milestone payments may be required for larger projects</li>
        <li>Final payment is due upon project completion and delivery</li>
        <li>Late payments may incur additional fees</li>
      </ul>
      
      <h3>5. Intellectual Property</h3>
      <p>Upon full payment, you will own the rights to the custom code and deliverables created specifically for your project. Alex Finger Consulting retains rights to:</p>
      <ul>
        <li>General methodologies and processes</li>
        <li>Pre-existing code libraries and frameworks</li>
        <li>Business strategies and consulting methodologies</li>
      </ul>
      
      <h3>6. Confidentiality</h3>
      <p>We maintain strict confidentiality regarding your business information, project details, and any proprietary information shared during our engagement.</p>
      
      <h3>7. Limitation of Liability</h3>
      <p>Our liability is limited to the amount paid for the specific service. We are not liable for indirect, incidental, or consequential damages.</p>
      
      <h3>8. Termination</h3>
      <p>Either party may terminate the agreement with written notice. Upon termination, you will be invoiced for work completed to date.</p>
      
      <h3>9. Changes to Terms</h3>
      <p>We reserve the right to modify these terms at any time. Continued use of our services constitutes acceptance of the modified terms.</p>
      
      <h3>10. Contact Information</h3>
      <p>For questions about these terms, please contact us on our <a href="/contact">contact page</a>.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

export const privacyPageComponents: ContentComponent[] = [
  {
    id: 'hero-privacy',
    type: 'hero',
    title: 'Privacy Policy',
    content: 'Your privacy is important to us. This policy explains how we collect, use, and protect your information.',
    metadata: {
      image: '/images/privacy.jpg'
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'privacy-content',
    type: 'text',
    title: 'Privacy Policy',
    content: `
      <p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>
      
      <h3>1. Information We Collect</h3>
      <p>We collect information you provide directly to us, such as when you:</p>
      <ul>
        <li>Contact us through our website or email</li>
        <li>Engage our services</li>
        <li>Participate in coaching or consulting sessions</li>
        <li>Subscribe to our newsletter or updates</li>
      </ul>
      
      <h3>2. Types of Information</h3>
      <p>The information we collect may include:</p>
      <ul>
        <li>Personal information (name, email address, phone number)</li>
        <li>Business information (company name, project details)</li>
        <li>Communication records (emails, meeting notes)</li>
        <li>Technical information (browser type, IP address for analytics)</li>
      </ul>
      
      <h3>3. How We Use Your Information</h3>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide and improve our services</li>
        <li>Communicate with you about projects and services</li>
        <li>Send you relevant updates and information</li>
        <li>Analyze website usage and improve user experience</li>
        <li>Comply with legal obligations</li>
      </ul>
      
      <h3>4. Information Sharing</h3>
      <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except:</p>
      <ul>
        <li>To trusted service providers who assist in our operations</li>
        <li>When required by law or to protect our rights</li>
        <li>In connection with a business transfer or acquisition</li>
      </ul>
      
      <h3>5. Data Security</h3>
      <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
      
      <h3>6. Data Retention</h3>
      <p>We retain your information for as long as necessary to provide our services and comply with legal obligations.</p>
      
      <h3>7. Your Rights</h3>
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal information</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion of your information</li>
        <li>Opt out of marketing communications</li>
      </ul>
      
      <h3>8. Cookies and Tracking</h3>
      <p>Our website may use cookies and similar tracking technologies to enhance your experience and analyze usage patterns.</p>
      
      <h3>9. Third-Party Services</h3>
      <p>We may use third-party services (such as Google Analytics) that have their own privacy policies. We encourage you to review these policies.</p>
      
      <h3>10. Changes to This Policy</h3>
      <p>We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page.</p>
      
      <h3>11. Contact Us</h3>
      <p>If you have questions about this privacy policy, please contact us on our <a href="/contact">contact page</a>.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

export const notFoundPageComponents: ContentComponent[] = [
  {
    id: 'hero-404',
    type: 'hero',
    title: '404 - Page Not Found',
    content: 'The page you\'re looking for doesn\'t exist or has been moved.',
    metadata: {
      image: '/images/showcase.jpg'
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: '404-content',
    type: 'text',
    title: 'Oops! We couldn\'t find that page',
    content: `
      <p>It looks like the page you're trying to reach doesn't exist. This could be because:</p>
      <ul>
        <li>The URL was typed incorrectly</li>
        <li>The page has been moved or deleted</li>
        <li>The link you followed is outdated</li>
      </ul>
      
      <h3>What can you do?</h3>
      <p>Here are some helpful links to get you back on track:</p>
      <ul>
        <li><a href="/">Go to Homepage</a> - Start from the beginning</li>
        <li><a href="/services">View Services</a> - See what we offer</li>
        <li><a href="/portfolio">Browse Portfolio</a> - Check out our work</li>
        <li><a href="/about">About Us</a> - Learn more about Alex Finger Consulting</li>
        <li><a href="/contact">Contact Us</a> - Get in touch with us</li>
      </ul>
      
      <p>If you believe this is an error or if you continue to experience issues, please <a href="/contact">contact us</a> and let us know.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];
